import { z } from 'zod'

import { EnumBuilder } from '@mntn-dev/utility-types'
import type { SetOptional } from 'type-fest'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  ExternalOrganizationTypeSchema,
  OrganizationTypeSchema,
} from './organization.models.ts'
import {
  CompanyNameSchema,
  EmailAddressSchema,
  NameSchema,
  NonEmptyStringSchema,
  TimestampSchema,
  USPhoneNumberSchema,
  WebsiteUrlSchema,
} from './property.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  FileIdSchema,
  OrganizationIdSchema,
  TeamIdSchema,
  UserIdSchema,
  UserUrnSchema,
} from './unique-id.models.ts'

/**
 * UserAuthConnection
 */
export const [
  UserAuthConnections,
  UserAuthConnectionSchema,
  UserAuthConnectionEnum,
] = EnumBuilder('email', 'Username-Password-Authentication').catch('email')

export type UserAuthConnection = z.infer<typeof UserAuthConnectionSchema>

/**
 * Bio
 */
export const BioSchema = z.string().trim().max(4096)
export type Bio = z.infer<typeof BioSchema>

/**
 * UserDisplayModel
 */
export const UserDisplayModelSchema = z.object({
  firstName: NameSchema,
  lastName: NameSchema,
  displayName: z.string(),
  initials: z.string(),
  avatarFileId: FileIdSchema.optional(),
})

export type UserDisplayModel = z.infer<typeof UserDisplayModelSchema>

export type UserDisplayModelComputedProperties = keyof Pick<
  UserDisplayModel,
  'displayName' | 'initials'
>

export const toUserDisplayModel = ({
  firstName,
  lastName,
  displayName,
  initials,
  avatarFileId,
}: SetOptional<
  UserDisplayModel,
  UserDisplayModelComputedProperties
>): UserDisplayModel => {
  const getDisplayName = () =>
    displayName ?? `${firstName.trim()} ${lastName.trim()}`

  const getInitials = () => {
    const [first = '-'] = firstName.trim()
    const [last = '-'] = lastName.trim()
    return initials ?? `${first}${last}`.toUpperCase()
  }

  return UserDisplayModelSchema.parse({
    firstName,
    lastName,
    displayName: getDisplayName(),
    initials: getInitials(),
    avatarFileId,
  })
}

/**
 * UserDomainSelectModel
 */
export const UserDomainSelectModelSchema = UserDisplayModelSchema.extend({
  userId: UserIdSchema,
  userUrn: UserUrnSchema,
  organizationId: OrganizationIdSchema,
  emailAddress: EmailAddressSchema,
  organizationType: OrganizationTypeSchema,
  bio: BioSchema.optional(),
  websiteUrls: WebsiteUrlSchema.array().optional(),
  phoneNumbers: USPhoneNumberSchema.array().optional(), // TODO: QF-2463 - Support international phone numbers
  authConnection: UserAuthConnectionSchema,
  isActive: z.boolean(),
  lastLogin: TimestampSchema.optional(),
  signUpTimestamp: TimestampSchema,
})

export type UserDomainSelectModel = z.infer<typeof UserDomainSelectModelSchema>

/**
 * UserDomainInsertModel
 */
export const UserDomainInsertModelSchema = UserDomainSelectModelSchema.omit({
  userUrn: true,
  isActive: true,
  displayName: true,
  initials: true,
  authConnection: true,
  lastLogin: true,
  signUpTimestamp: true,
}).partial({
  userId: true,
})

export type UserDomainInsertModel = z.infer<typeof UserDomainInsertModelSchema>
/**
 * UserDomainRegistrationModel
 * userId and emailAddress values are extracted from the session object
 */
export const UserDomainRegistrationModelSchema =
  UserDomainInsertModelSchema.omit({
    userId: true,
    emailAddress: true,
  })

export type UserDomainRegistrationModel = z.infer<
  typeof UserDomainRegistrationModelSchema
>

/**
 * UserDomainUpdateModel
 */
export const UserDomainUpdateModelSchema = UserDomainSelectModelSchema.omit({
  userId: true,
  userUrn: true,
  organizationId: true,
  displayName: true,
  initials: true,
  avatarFileId: true,
  signUpTimestamp: true,
})
  // Certain properties are nullable so they can be removed
  .extend({
    avatarFileId: UserDomainSelectModelSchema.shape.avatarFileId.nullable(),
  })
  .partial()

export type UserDomainUpdateModel = z.infer<typeof UserDomainUpdateModelSchema>

/**
 * ExternalUserDomainUpdateModel
 */
export const ExternalUserDomainUpdateModelSchema =
  UserDomainUpdateModelSchema.extend({
    organizationType: ExternalOrganizationTypeSchema,
  }).partial()

/**
 * UserDomainSelectModelFilters
 */
export const UserDomainSelectModelFiltersSchema = z
  .object({
    userId: ModelFilterSchema(UserIdSchema),
    userUrn: ModelFilterSchema(UserUrnSchema),
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    // When filtering, transform email addresses to lower case so the
    // "lower(users.email_address)" unique index can be used.
    emailAddress: ModelFilterSchema(EmailAddressSchema.toLowerCase()),
    organizationType: ModelFilterSchema(OrganizationTypeSchema),
    search: NonEmptyStringSchema(),
    includeDeactivated: z.boolean(),
    tags: TagFilterSchema,
  })
  .partial()

export type UserDomainSelectModelFilters = z.infer<
  typeof UserDomainSelectModelFiltersSchema
>

/**
 * TODO: The following schemas/types should probably be moved to the user-service package
 */
export const UserCompanyInsertUpdateSchema = z.object({
  userId: UserIdSchema.optional(),
  organizationType: OrganizationTypeSchema,
  firstName: NameSchema,
  lastName: NameSchema,
  emailAddress: EmailAddressSchema.optional(),
  companyName: CompanyNameSchema.optional(),
  teamId: TeamIdSchema.optional(),
  bio: BioSchema.optional(),
  websiteUrls: WebsiteUrlSchema.array().optional(),
  phoneNumbers: USPhoneNumberSchema.array().optional(),
})

export type UserCompanyInsertUpdateType = z.infer<
  typeof UserCompanyInsertUpdateSchema
>

export const UserDomainSelectModelWithDeactivateStatusSchema =
  UserDomainSelectModelSchema.extend({
    canBeDeactivated: z.boolean(),
  })

export type UserDomainSelectModelWithDeactivateStatusType = z.infer<
  typeof UserDomainSelectModelWithDeactivateStatusSchema
>
